// Per-study 개인정보 동의문 editor (the "🔒 개인정보 동의문" toolbar button).
// Korean and English are edited and saved independently; a language that has never been
// saved shows the default from consentTemplates.js.
//
// Structured after EmailTemplateModal — same chrome, same two-tab save model — and it
// reuses EmailComposer for the editing itself, with the mail-specific trim switched off.
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import EmailComposer from "./EmailComposer";
import { DEFAULT_CONSENT_TEMPLATES, CONSENT_LANGS } from "./consentTemplates";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn,
  LangTabs, LangTab, Loading,
} from "./emailModalUI";

const Meta = styled.div`
  font-size: 11px;
  color: #adb5bd;
  align-self: center;
  margin-right: auto;
`;

const COMPOSER_NOTE = (
  <>
    이 동의문은 Site가 비밀번호를 입력한 뒤 <b>설문을 열기 전에</b> 표시되며, 동의해야 설문이 열립니다.
    <br />
    동의 시점의 제목과 본문은 그대로 보관되므로, 이후 이 템플릿을 고쳐도 이미 받은 동의 기록은 바뀌지 않습니다.
  </>
);

export default function ConsentTemplateModal({ sfqId, onClose }) {
  const [lang, setLang]     = useState("ko");
  const [drafts, setDrafts] = useState(null);   // { ko: {title, bodyHtml}, en: {...} }
  const [meta, setMeta]     = useState({});     // { ko: {updatedBy, updatedAt}, ... }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    sfqApi.getConsentTemplate(sfqId)
      .then((res) => {
        if (!alive) return;
        const saved = res.data.data || {};
        // No saved row for a language → seed the editor from the frontend default.
        setDrafts({
          ko: pick(saved.ko) || { ...DEFAULT_CONSENT_TEMPLATES.ko },
          en: pick(saved.en) || { ...DEFAULT_CONSENT_TEMPLATES.en },
        });
        setMeta({ ko: saved.ko, en: saved.en });
      })
      .catch(() => {
        if (alive) setDrafts({
          ko: { ...DEFAULT_CONSENT_TEMPLATES.ko },
          en: { ...DEFAULT_CONSENT_TEMPLATES.en },
        });
      });
    return () => { alive = false; };
  }, [sfqId]);

  function pick(row) {
    return row ? { title: row.title, bodyHtml: row.bodyHtml } : null;
  }

  // EmailComposer speaks { subject, bodyHtml }; the consent text calls that field a title.
  function patch(next) {
    const mapped = "subject" in next ? { title: next.subject } : next;
    setDrafts((d) => ({ ...d, [lang]: { ...d[lang], ...mapped } }));
  }

  function resetToDefault() {
    if (!window.confirm(`${CONSENT_LANGS.find((l) => l.key === lang).label} 동의문을 기본값으로 되돌립니다. 계속할까요?`)) return;
    setDrafts((d) => ({ ...d, [lang]: { ...DEFAULT_CONSENT_TEMPLATES[lang] } }));
  }

  async function handleSave() {
    const draft = drafts[lang];
    if (!draft.title?.trim()) { alert("동의문 제목을 입력해 주세요."); return; }
    setSaving(true);
    try {
      await sfqApi.saveConsentTemplate(sfqId, { lang, title: draft.title, bodyHtml: draft.bodyHtml });
      alert("동의문이 저장되었습니다.");
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || "동의문 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  }

  const savedAt = meta[lang]?.updatedAt;

  return (
    <Backdrop>
      <Modal $wide>
        <Header>
          <Title>개인정보 수집·이용 동의문</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        <Body>
          <LangTabs>
            {CONSENT_LANGS.map((l) => (
              <LangTab key={l.key} type="button" $active={lang === l.key} onClick={() => setLang(l.key)}>
                {l.label}
                {!meta[l.key] && <span title="아직 저장된 적 없음 (기본값)"> ·</span>}
              </LangTab>
            ))}
          </LangTabs>

          {!drafts ? (
            <Loading>불러오는 중...</Loading>
          ) : (
            <EmailComposer
              sfqId={sfqId}
              subject={drafts[lang].title}
              bodyHtml={drafts[lang].bodyHtml}
              onChange={patch}
              vars={{}}
              varList={[]}
              requireLink={false}
              subjectLabel="동의문 제목"
              note={COMPOSER_NOTE}
              // 동의문에는 치환할 변수가 없어 미리보기가 편집창의 사본에 그칠 뿐 아니라,
              // 메일 셸로 그려져 실제 동의 화면(SiteConsentGate)과도 다르게 보인다.
              showPreview={false}
            />
          )}
        </Body>

        <Footer>
          <Meta>
            {savedAt
              ? `최종 수정: ${new Date(savedAt).toLocaleString("ko-KR")}`
              : "저장된 동의문 없음 — 기본값을 표시하고 있습니다."}
          </Meta>
          <Btn type="button" onClick={resetToDefault} disabled={!drafts || saving}>기본값으로</Btn>
          <Btn type="button" onClick={onClose} disabled={saving}>닫기</Btn>
          <Btn type="button" $primary onClick={handleSave} disabled={!drafts || saving}>
            {saving ? "저장 중..." : "저장"}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
